import { supabase } from '@/integrations/supabase/client';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import type { Json } from '@/integrations/supabase/types';

/**
 * Job states:
 *   idle        – no active job
 *   queued      – lock acquired, about to start
 *   processing  – backend work in progress
 *   reconnecting – UI re-attaching to an in-progress job after refresh
 *   completed   – job finished successfully
 *   failed      – job finished with error
 *   cancelled   – job was cancelled
 *   stale       – job exceeded TTL without completing
 */
export type JobStatus = 'idle' | 'queued' | 'processing' | 'reconnecting' | 'completed' | 'failed' | 'cancelled' | 'stale';

export interface JobLock {
  lockId: string;
  jobKey: string;
  status: JobStatus;
  createdAt: string;
  expiresAt: string;
  resultJson?: Json;
  errorMessage?: string;
}

export interface AcquireResult {
  acquired: boolean;
  reconnected?: boolean;
  reuse_result?: boolean;
  lock_id: string;
  status: string;
  created_at?: string;
  expires_at?: string;
  result_json?: Json;
  retry?: boolean;
}

function getClient(portal: 'hire' | 'seek' | 'admin') {
  return portal === 'seek' ? seekSupabase : supabase;
}

/**
 * Acquire a job lock atomically at the database level.
 * Returns lock info or reconnects to existing in-progress job.
 */
export async function acquireJobLock(
  jobKey: string,
  jobType: string,
  ownerId: string,
  portal: 'hire' | 'seek' | 'admin' = 'hire',
  ttlSeconds: number = 300
): Promise<AcquireResult> {
  const client = getClient(portal);
  const { data, error } = await client.rpc('acquire_job_lock', {
    _job_key: jobKey,
    _job_type: jobType,
    _owner_id: ownerId,
    _ttl_seconds: ttlSeconds,
  });

  if (error) throw new Error(`Lock acquisition failed: ${error.message}`);
  return data as unknown as AcquireResult;
}

/**
 * Release a job lock (mark as completed, failed, or cancelled).
 */
export async function releaseJobLock(
  lockId: string,
  status: 'completed' | 'failed' | 'cancelled' = 'completed',
  portal: 'hire' | 'seek' | 'admin' = 'hire',
  resultJson?: Json,
  errorMessage?: string
): Promise<boolean> {
  const client = getClient(portal);
  const { data, error } = await client.rpc('release_job_lock', {
    _lock_id: lockId,
    _status: status,
    _result_json: resultJson ?? null,
    _error_message: errorMessage ?? null,
  });

  if (error) throw new Error(`Lock release failed: ${error.message}`);
  return data as unknown as boolean;
}

/**
 * Send a heartbeat to extend lock TTL (call periodically during long jobs).
 */
export async function heartbeatJobLock(
  lockId: string,
  portal: 'hire' | 'seek' | 'admin' = 'hire'
): Promise<boolean> {
  const client = getClient(portal);
  const { data, error } = await client.rpc('heartbeat_job_lock', {
    _lock_id: lockId,
  });

  if (error) return false;
  return data as unknown as boolean;
}

/**
 * Check if there's an active lock for a given job key.
 * Used for reconnecting on page refresh/re-entry.
 */
export async function getActiveJobLock(
  jobKey: string,
  ownerId: string,
  portal: 'hire' | 'seek' | 'admin' = 'hire'
): Promise<JobLock | null> {
  const client = getClient(portal);
  const { data, error } = await client
    .from('async_job_locks')
    .select('*')
    .eq('job_key', jobKey)
    .eq('owner_id', ownerId)
    .in('status', ['queued', 'processing'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;

  // Check if expired client-side
  if (new Date(data.expires_at) < new Date()) {
    return null;
  }

  return {
    lockId: data.id,
    jobKey: data.job_key,
    status: data.status as JobStatus,
    createdAt: data.created_at,
    expiresAt: data.expires_at,
    resultJson: data.result_json,
    errorMessage: data.error_message,
  };
}

/**
 * Poll a lock until it reaches a terminal state.
 * Useful for reconnecting to an in-progress job.
 */
export async function pollJobLockUntilDone(
  lockId: string,
  portal: 'hire' | 'seek' | 'admin' = 'hire',
  intervalMs: number = 2000,
  maxAttempts: number = 150
): Promise<JobLock> {
  const client = getClient(portal);

  for (let i = 0; i < maxAttempts; i++) {
    const { data, error } = await client
      .from('async_job_locks')
      .select('*')
      .eq('id', lockId)
      .single();

    if (error) throw new Error(`Poll failed: ${error.message}`);

    const status = data.status as JobStatus;
    if (['completed', 'failed', 'cancelled', 'stale'].includes(status)) {
      return {
        lockId: data.id,
        jobKey: data.job_key,
        status,
        createdAt: data.created_at,
        expiresAt: data.expires_at,
        resultJson: data.result_json,
        errorMessage: data.error_message,
      };
    }

    await new Promise(r => setTimeout(r, intervalMs));
  }

  throw new Error('Job polling timed out');
}

// Session storage key prefix for client-side lock tracking
const STORAGE_PREFIX = 'async_job_lock:';

export function saveJobLockToSession(jobKey: string, lockId: string) {
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${jobKey}`, lockId);
  } catch { /* quota exceeded */ }
}

export function getJobLockFromSession(jobKey: string): string | null {
  try {
    return sessionStorage.getItem(`${STORAGE_PREFIX}${jobKey}`);
  } catch {
    return null;
  }
}

export function clearJobLockFromSession(jobKey: string) {
  try {
    sessionStorage.removeItem(`${STORAGE_PREFIX}${jobKey}`);
  } catch { /* ignore */ }
}
